import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  FormHelperText,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  TrendingUp as TrendingUpIcon,
  Palette as PaletteIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import { extractLeadStageFields } from '../../../api/leadStage.api.js';

const colorOptions = [
  { value: 'primary', label: 'Primary (Blue)', color: '#1976d2' },
  { value: 'secondary', label: 'Secondary (Pink)', color: '#dc004e' },
  { value: 'success', label: 'Success (Green)', color: '#2e7d32' },
  { value: 'error', label: 'Error (Red)', color: '#d32f2f' },
  { value: 'warning', label: 'Warning (Orange)', color: '#ed6c02' },
  { value: 'info', label: 'Info (Light Blue)', color: '#0288d1' },
  { value: 'default', label: 'Default (Gray)', color: '#757575' },
];

function LeadStageForm({
  leadStage, onSave, onCancel, loading = false,
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: 'primary',
    order: '',
  });

  const [errors, setErrors] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [nlpProvider, setNlpProvider] = useState('openai');
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    if (leadStage) {
      setFormData({
        name: leadStage.name || '',
        description: leadStage.description || '',
        color: leadStage.color || 'primary',
        order: leadStage.order || '',
      });
    }
  }, [leadStage]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Stage name is required';
    }
    return newErrors;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const leadStageData = {
      ...formData,
      order: formData.order ? parseInt(formData.order) : null,
    };

    onSave(leadStageData);
  };

  const startListening = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        setTranscript('');
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        setTranscript(finalTranscript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    } else {
      alert('Speech recognition is not supported in this browser.');
    }
  };

  const stopListening = () => {
    if ('webkitSpeechRecognition' in window) {
      window.webkitSpeechRecognition.abort();
    }
  };

  const handleExtractFields = async () => {
    if (!transcript.trim()) {
      alert('Please provide some text to extract fields from.');
      return;
    }

    setExtracting(true);
    try {
      const result = await extractLeadStageFields(transcript, nlpProvider);
      if (result.success && result.fields) {
        const extractedData = { ...formData };

        if (result.fields.name) extractedData.name = result.fields.name;
        if (result.fields.description) extractedData.description = result.fields.description;
        if (result.fields.color) extractedData.color = result.fields.color;
        if (result.fields.order) extractedData.order = result.fields.order;

        setFormData(extractedData);
        setTranscript('');
        alert('Fields extracted successfully!');
      }
    } catch (error) {
      console.error('Error extracting fields:', error);
      alert('Failed to extract fields. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <Stack spacing={3} sx={{ mt: 1 }}>
        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TrendingUpIcon color="primary" />
              <Typography variant="h6">
                {leadStage ? 'Edit Lead Stage' : 'Add New Lead Stage'}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Stage Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                placeholder="e.g., Qualified Lead, Proposal Sent"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TrendingUpIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe this lead stage..."
              />

              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Color Theme</InputLabel>
                  <Select
                    value={formData.color}
                    label="Color Theme"
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    startAdornment={(
                      <InputAdornment position="start">
                        <PaletteIcon color="action" />
                      </InputAdornment>
                    )}
                  >
                    {colorOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: option.color,
                            }}
                          />
                          {option.label}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Choose a color to represent this stage
                  </FormHelperText>
                </FormControl>

                <TextField
                  fullWidth
                  label="Display Order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => handleInputChange('order', e.target.value)}
                  placeholder="1, 2, 3..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SortIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Voice Input & NLP */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
              <MicIcon color="primary" />
              <Typography variant="h6">Voice Input & AI Extraction</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <Alert severity="info">
                Use voice input to quickly create lead stages. The AI will extract relevant fields from your speech.
              </Alert>

              <FormControl fullWidth>
                <InputLabel>NLP Provider</InputLabel>
                <Select
                  value={nlpProvider}
                  label="NLP Provider"
                  onChange={(e) => setNlpProvider(e.target.value)}
                >
                  <MenuItem value="openai">OpenAI GPT</MenuItem>
                  <MenuItem value="llama4">Llama 4 (Together AI)</MenuItem>
                  <MenuItem value="ollama">Ollama (Local)</MenuItem>
                </Select>
                <FormHelperText>
                  Choose the AI model for field extraction
                </FormHelperText>
              </FormControl>

              <Box>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <IconButton
                    onClick={isListening ? stopListening : startListening}
                    color={isListening ? 'error' : 'primary'}
                    size="large"
                  >
                    {isListening ? <MicOffIcon /> : <MicIcon />}
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {isListening ? 'Listening... Click to stop' : 'Click to start voice input'}
                  </Typography>
                </Stack>

                {transcript && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Voice Transcript"
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}

                {transcript && (
                  <Button
                    variant="outlined"
                    onClick={handleExtractFields}
                    disabled={extracting}
                    startIcon={<TrendingUpIcon />}
                  >
                    {extracting ? 'Extracting...' : 'Extract Fields'}
                  </Button>
                )}
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons */}
        <Box sx={{
          display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3,
        }}
        >
          <Button onClick={onCancel} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : (leadStage ? 'Update' : 'Save')}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}

export default LeadStageForm;
